import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

export default function Layout() {
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  async function handleLogout() {
    await logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  }

  return (
    <div className="font-sans min-h-screen flex flex-col bg-gray-950 text-gray-100 overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-gray-950 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="text-xl font-bold text-white tracking-tight">
              Clip Looper
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/tool" className="text-gray-300 hover:text-white transition-colors">
                Free Tool
              </Link>

              {!isAuthenticated ? (
                <>
                  <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">
                    Pricing
                  </Link>
                  <Link to="/login" className="text-gray-300 hover:text-white transition-colors">
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Sign Up
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/dashboard" className="text-gray-300 hover:text-white transition-colors">
                    Dashboard
                  </Link>

                  {/* User menu */}
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center text-gray-300 hover:text-white bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <span className="text-sm">{user?.email || 'Account'}</span>
                      <svg
                        className={`ml-1 h-4 w-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {userMenuOpen && (
                      <div
                        className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 z-50"
                        style={{ background: '#111827', border: '1px solid #374151' }}
                      >
                        <Link
                          to="/dashboard"
                          onClick={() => setUserMenuOpen(false)}
                          style={{ display: 'block', padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#d1d5db', background: 'transparent', textDecoration: 'none' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#1f2937'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#d1d5db'; }}
                        >
                          Manage Subscription
                        </Link>
                        <button
                          onClick={handleLogout}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#d1d5db', background: 'transparent', border: 'none', cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#1f2937'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#d1d5db'; }}
                        >
                          Log Out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Mobile hamburger button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-300 hover:text-white p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-800">
            <div className="px-4 py-3 space-y-2">
              <Link
                to="/tool"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-gray-300 hover:text-white py-3 transition-colors"
              >
                Free Tool
              </Link>

              {!isAuthenticated ? (
                <>
                  <Link
                    to="/pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-300 hover:text-white py-3 transition-colors"
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-300 hover:text-white py-3 transition-colors"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block bg-indigo-600 hover:bg-indigo-500 text-white text-center px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                  >
                    Sign Up
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-300 hover:text-white py-3 transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-300 hover:text-white py-3 transition-colors"
                  >
                    Manage Subscription
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left text-gray-300 hover:text-white py-3 transition-colors"
                  >
                    Log Out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="bg-gray-950 text-gray-100 min-h-screen flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Clip Looper. All rights reserved.</p>
          <a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors mt-2 sm:mt-0">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
}
